/**
 * Prime24 sale card — tags ↔ product-carousel sync + panel interactions (vanilla).
 */
if (!customElements.get('prime24-sale-card')) {
  class Prime24SaleCard extends HTMLElement {
    /** @type {number} */
    _tries = 0;

    /** @type {(HTMLElement & { swiper?: { activeIndex: number; on: Function; off: Function; slideTo: Function } }) | null} */
    _carousel = null;

    /** @type {(() => void) | null} */
    _onSlideChange = null;

    connectedCallback() {
      this._onTagClick = this._onTagClick.bind(this);
      this._onPanelClick = this._onPanelClick.bind(this);
      this._onSelectChange = this._onSelectChange.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._tries = 0;
      this._bindPanelEvents();
      this._initWhenCarouselReady();
    }

    disconnectedCallback() {
      this.querySelectorAll('.product_tags_list .tag_item').forEach((btn) => {
        btn.removeEventListener('click', this._onTagClick);
      });
      this.removeEventListener('click', this._onPanelClick);
      this.removeEventListener('change', this._onSelectChange);
      this.removeEventListener('submit', this._onSubmit, true);
      if (this._carousel?.swiper && this._onSlideChange) {
        this._carousel.swiper.off('slideChange', this._onSlideChange);
        this._carousel.swiper.off('init', this._onSlideChange);
      }
    }

    _bindPanelEvents() {
      this.addEventListener('click', this._onPanelClick);
      this.addEventListener('change', this._onSelectChange);
      this.addEventListener('submit', this._onSubmit, true);
      this._clampDescriptions();
    }

    /**
     * Native {% form 'product' %} posts to /cart/add and redirects.
     * Intercept and use the Ajax Cart API instead.
     * @param {SubmitEvent} event
     */
    _onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = form.getAttribute('action') || '';
      const isProductForm =
        form.classList.contains('product-form') ||
        action.indexOf('/cart/add') !== -1 ||
        !!form.querySelector('select[data-product-select], input[name="id"]');
      if (!isProductForm) return;

      const submitter = /** @type {HTMLElement | null} */ (event.submitter);
      if (
        submitter &&
        (submitter.closest('shopify-accelerated-checkout') ||
          submitter.closest('.shopify-payment-button') ||
          submitter.getAttribute('name') === 'checkout')
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const atcButton =
        (submitter && submitter.matches('[data-add-to-cart], button[name="add"]') && submitter) ||
        form.querySelector('[data-add-to-cart], button[type="submit"][name="add"]');
      this._setButtonLoading(atcButton, true);
      this._addFormToCart(form)
        .then(() => {
          this._showAddNotice(form);
        })
        .catch((err) => {
          console.error(err);
          this._showAddNotice(form, err.message || 'Could not add to cart');
        })
        .finally(() => {
          this._setButtonLoading(atcButton, false);
        });
    }

    /**
     * @param {Element | null} btn
     * @param {boolean} loading
     */
    _setButtonLoading(btn, loading) {
      if (!btn) return;
      const button = /** @type {HTMLButtonElement} */ (btn);
      const label = button.querySelector('[data-add-to-cart-text]') || button;
      const addingText =
        (window.Theme && Theme.translations && Theme.translations.adding_to_cart) || 'Adding...';

      button.disabled = !!loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      button.classList.toggle('is-loading', !!loading);

      if (loading) {
        if (!button.dataset.previousLabel) {
          button.dataset.previousLabel = label.textContent?.trim() || '';
        }
        label.textContent = addingText;
        return;
      }

      if (button.dataset.previousLabel) {
        label.textContent = button.dataset.previousLabel;
        delete button.dataset.previousLabel;
      }
    }

    /**
     * @returns {string[]}
     */
    _getCartSectionIds() {
      const ids = [];
      document.querySelectorAll('cart-items-component').forEach((el) => {
        const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids;
    }

    _openCartDrawer() {
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer) return;
      if (typeof /** @type {{ open?: Function }} */ (drawer).open === 'function') {
        /** @type {{ open: Function }} */ (drawer).open();
        return;
      }
      if (typeof /** @type {{ showDialog?: Function }} */ (drawer).showDialog === 'function') {
        /** @type {{ showDialog: Function }} */ (drawer).showDialog();
      }
    }

    /**
     * @param {HTMLFormElement} form
     */
    async _addFormToCart(form) {
      const formData = new FormData(form);
      if (!formData.get('quantity')) formData.set('quantity', '1');

      const sectionIds = this._getCartSectionIds();
      if (sectionIds.length) {
        formData.set('sections', sectionIds.join(','));
        formData.set('sections_url', window.location.pathname);
      }

      const addUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const data = await res.json();
      if (data.status && (data.description || data.message)) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart || {},
            sourceId: 'prime24-sale-card',
            data: {
              source: 'prime24-sale-card',
              variantId: formData.get('id') ? String(formData.get('id')) : undefined,
              itemCount: Number(cart.item_count) || 0,
              sections: data.sections || {},
            },
          },
        })
      );

      this._openCartDrawer();
      return data;
    }

    /**
     * @param {unknown} value
     * @returns {string}
     */
    _escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    /**
     * @param {HTMLFormElement} form
     * @param {string} [errorMessage]
     */
    _showAddNotice(form, errorMessage) {
      form.querySelectorAll('.ajax-add-notice').forEach((el) => el.remove());

      const notice = document.createElement('div');
      notice.className = errorMessage
        ? 'ajax-add-notice ajax-add-notice--error global-border-radius'
        : 'ajax-add-notice ajax-add-notice--added global-border-radius';

      if (errorMessage) {
        notice.innerHTML = `<div class="ajax-add-notice__inner">${this._escapeHtml(errorMessage)}</div>`;
      } else {
        const added = (window.Theme && Theme.translations && Theme.translations.added) || 'Added';
        const goToCart =
          (window.Theme && Theme.translations && Theme.translations.go_to_cart) || 'Go to cart';
        const cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
        notice.innerHTML = [
          '<div class="ajax-add-notice__inner">',
          '<div class="ajax-add-notice__item">',
          '<span class="feather-icon">',
          '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon feather-check"><polyline points="20 6 9 17 4 12"></polyline></svg>',
          '</span>',
          this._escapeHtml(added),
          '</div>',
          '<div class="ajax-add-notice__go">',
          `<a href="${this._escapeHtml(cartUrl)}">`,
          this._escapeHtml(goToCart),
          '<span class="feather-icon feather-icon--small">',
          '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon feather-chevron-right"><polyline points="9 18 15 12 9 6"></polyline></svg>',
          '</span>',
          '</a>',
          '</div>',
          '</div>',
        ].join('');
      }

      form.appendChild(notice);
    }

    _initWhenCarouselReady() {
      this._carousel = /** @type {Prime24SaleCard['_carousel']} */ (
        this.querySelector('product-carousel')
      );
      if (this._carousel?.swiper) {
        this._bindCarousel();
        return;
      }
      this._tries += 1;
      if (this._tries > 80) return;
      window.setTimeout(() => this._initWhenCarouselReady(), 25);
    }

    _bindCarousel() {
      const swiper = this._carousel?.swiper;
      if (!swiper) return;

      this._onSlideChange = () => this._syncTags(swiper.activeIndex);
      swiper.on('slideChange', this._onSlideChange);
      this._syncTags(swiper.activeIndex);

      this.querySelectorAll('.product_tags_list .tag_item').forEach((btn) => {
        btn.addEventListener('click', this._onTagClick);
      });
    }

    /**
     * @param {number} index
     */
    _syncTags(index) {
      this.querySelectorAll('.product_tags_list .tag_item').forEach((btn, i) => {
        btn.classList.toggle('active', i === index);
      });
    }

    /**
     * @param {Event} event
     */
    _onTagClick(event) {
      const current = /** @type {Element | null} */ (event.currentTarget);
      if (!current) return;
      const tags = Array.from(this.querySelectorAll('.product_tags_list .tag_item'));
      const index = tags.indexOf(current);
      if (index < 0 || !this._carousel?.swiper) return;
      this._carousel.swiper.slideTo(index);
    }

    /**
     * @param {MouseEvent} event
     */
    _onPanelClick(event) {
      const target = /** @type {Element | null} */ (event.target);
      if (!target) return;

      if (target.closest('discount-code-copy, .product-discount-full__btn-copy')) {
        return;
      }

      const likeBtn = target.closest('.product-like-btn');
      if (likeBtn && this.contains(likeBtn)) {
        likeBtn.classList.toggle('active');
        return;
      }

      const qtyMinus = target.closest('.qty_minus');
      if (qtyMinus && this.contains(qtyMinus)) {
        const input = /** @type {HTMLInputElement | null} */ (
          qtyMinus.parentElement?.querySelector('.quantity-actual')
        );
        if (!input) return;
        const val = parseInt(input.value, 10) || 1;
        if (val > 1) input.value = String(val - 1);
        return;
      }

      const qtyPlus = target.closest('.qty_plus');
      if (qtyPlus && this.contains(qtyPlus)) {
        const input = /** @type {HTMLInputElement | null} */ (
          qtyPlus.parentElement?.querySelector('.quantity-actual')
        );
        if (!input) return;
        const val = parseInt(input.value, 10) || 1;
        input.value = String(val + 1);
        return;
      }

      const moreDesc = target.closest('.product_moredesc');
      if (moreDesc && this.contains(moreDesc)) {
        const desc =
          moreDesc
            .closest('.product-form__wrapper, .product-top, .prime-day-sale-layout__info')
            ?.querySelector('.product_desc') || moreDesc.previousElementSibling;
        const targetDesc =
          /** @type {HTMLElement | null} */ (
            moreDesc.parentElement?.querySelector('.product_desc') || desc
          );
        if (targetDesc) targetDesc.style.height = 'auto';
        /** @type {HTMLElement} */ (moreDesc).style.display = 'none';
        return;
      }

      const clicky = target.closest('.clickyboxes[data-single-option-selector] > li');
      if (clicky && this.contains(clicky)) {
        this._handleClickybox(clicky);
      }
    }

    /**
     * @param {Element} li
     */
    _handleClickybox(li) {
      const selectorWrapper = li.closest('.selector-wrapper');
      if (!selectorWrapper) return;
      const options = selectorWrapper.querySelectorAll('select[data-single-option-selector] option');
      const parent = li.parentElement;
      if (!parent) return;
      const index = Array.from(parent.children).indexOf(li);
      const option = /** @type {HTMLOptionElement | undefined} */ (options[index]);
      if (option) option.selected = true;

      selectorWrapper.querySelectorAll('.clickyboxes[data-single-option-selector] > li > a').forEach((a) => {
        a.classList.remove('active');
      });
      const link = li.querySelector('a');
      if (link) {
        link.classList.add('active');
        const title = selectorWrapper.querySelector('label > .variant-option-title');
        if (title) title.textContent = link.getAttribute('data-value') || '';
      }
      this._syncProductSelect(li.closest('.product-form__wrapper'));
    }

    /**
     * @param {Event} event
     */
    _onSelectChange(event) {
      const select = /** @type {HTMLElement} */ (event.target);
      if (!select.matches?.('select[data-single-option-selector]')) return;
      this._syncProductSelect(select.closest('.product-form__wrapper'));
    }

    /**
     * @param {Element | null} wrapper
     */
    _syncProductSelect(wrapper) {
      if (!wrapper) return;
      const allSelects = wrapper.querySelectorAll('select[data-single-option-selector]');
      const selectedValues = Array.from(allSelects).map(
        (el) => /** @type {HTMLSelectElement} */ (el).value
      );
      wrapper.querySelectorAll('select[data-product-select] option').forEach((opt) => {
        const option = /** @type {HTMLOptionElement} */ (opt);
        const label = option.label || option.textContent || '';
        const match = selectedValues.every((v) => label.indexOf(v) !== -1);
        if (match) option.selected = true;
      });
    }

    _clampDescriptions() {
      if (this.dataset.showSeeMore !== 'true') return;
      if (window.innerWidth <= 1000) return;

      this.querySelectorAll('.product_desc').forEach((desc) => {
        const el = /** @type {HTMLElement} */ (desc);
        const inner = /** @type {HTMLElement | null} */ (
          el.querySelector(':scope > div') || el.firstElementChild
        );
        if (!inner) return;
        const lineHeight = parseInt(getComputedStyle(inner).lineHeight, 10) || 24;
        el.style.height = `${lineHeight * 3}px`;
        const more = /** @type {HTMLElement | null} */ (
          el.parentElement?.querySelector('.product_moredesc')
        );
        if (!more) return;
        more.style.display = inner.scrollHeight > el.clientHeight ? '' : 'none';
      });
    }
  }

  customElements.define('prime24-sale-card', Prime24SaleCard);
}
